import { useState, type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Location, Order } from "../../models";
import { Tooltip } from "flowbite-react";
import { FaRegCopy } from "react-icons/fa";
import { HiCheck } from "react-icons/hi";
import { OrderKPIs, RelatedOrders, SimpleList } from "@components";
import { useGlobalOrders } from "../../hooks";
import { DetailHeader, Inline, Text } from "@design-system";

export interface LocationDetailsProps {
  location: Location | undefined;
  sendInvoice?: () => void;
  edit: () => void;
  delete: () => void;
}

export const LocationDetails: FC<LocationDetailsProps> = (props) => {
  const { t } = useTranslation(["common", "location"]);
  const [hasCopied, setHasCopied] = useState(false);

  // Hent alle ordrer for arbeidsområdet using global hook for cost optimization
  const query = useGlobalOrders();
  const allOrders = useMemo(() => query.data ?? [], [query.data]) as Order[];

  // Filtrer ordrer basert på den valgte lokasjonen
  const orders = useMemo(() => {
    if (!props.location || !props.location.id) return [];

    return allOrders.filter(
      (order) =>
        order.route &&
        order.route.some(
          (routeItem) =>
            routeItem.location && routeItem.location.id === props.location?.id,
        ),
    );
  }, [allOrders, props.location]);

  const isQueryLoading = useMemo(
    () => query.isLoading && !query.data,
    [query.isLoading, query.data],
  );

  return (
    <>
      {/* Location Header */}

      <DetailHeader>
        <Inline gap={4}>
          <Text as="p" variant="detail-title">
            {props.location?.displayName}
          </Text>

          {!hasCopied ? (
            <Tooltip content={t("common:copy")}>
              <div
                className="bg-none rounded-md relative"
                style={{ width: "34px", height: "34px" }}
              >
                <FaRegCopy
                  className="w-6 h-6 dark:text-white cursor-pointer absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      props.location?.displayName?.toString() ?? "",
                    );
                    setHasCopied(true);
                  }}
                />
              </div>
            </Tooltip>
          ) : (
            <div className="flex items-center text-muted dark:text-text">
              <HiCheck className="mr-2 h-5 w-5" />
              <p>{t("common:copied")}</p>
            </div>
          )}
        </Inline>
      </DetailHeader>

      <OrderKPIs
        orders={orders}
        loading={isQueryLoading}
        isLocationDetails={true}
      />

      <SimpleList
        items={[
          {
            title: t("location:details.address"),
            value: props.location?.addressLine,
          },
          {
            title: t("location:details.city"),
            value: props.location?.city,
          },
          {
            title: t("location:details.postCode"),
            value: props.location?.postCode,
          },
          {
            title: t("location:details.description"),
            value: props.location?.description,
          },
          {
            title: t("location:details.countryCode"),
            value: props.location?.countryCode,
          },
          {
            title: t("location:contacts.title"),
            value:
              props.location?.contacts
                ?.map(
                  (contact) => `${contact.name}: ${contact.phoneNumber}`,
                )
                .join(", ") || t("location:contacts.none"),
          },
        ]}
      />

      <RelatedOrders orders={orders} loading={isQueryLoading} />
    </>
  );
};
